import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API from '../utils/API';
import { sortBillsByMode } from '../utils/billSortGrouping';
import { deriveBillDisplayFields } from '../utils/billDisplay';
import { downloadReportAsPDF } from '../utils/reportExport';
import './createReportModal.css';

const INTRO_QUILL_MODULES = {
    toolbar: [
        [{ header: [false, 2, 3] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};
const INTRO_QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Quill leaves behind markup like "<p><br></p>" when the editor is visually empty.
function isIntroMessageEmpty(html) {
    return !html || html.replace(/<(.|\n)*?>/g, '').trim().length === 0;
}

export default function CreateReportModal({ cart, sortMode, eligibleTeams, onClose, onCreated, onRemoveBill, onNoteChange }) {
    const hasTeamChoice = eligibleTeams.length > 1;
    const STEPS = hasTeamChoice ? ['Team', 'Bills & Notes', 'Format'] : ['Bills & Notes', 'Format'];

    const [step, setStep] = useState(0);
    const [selectedTeamId, setSelectedTeamId] = useState(hasTeamChoice ? null : eligibleTeams[0]?.team_id ?? null);

    const [title, setTitle] = useState('');
    const [introMessage, setIntroMessage] = useState('');
    const [showDigest, setShowDigest] = useState(false);
    const [showAiSummary, setShowAiSummary] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    // Post-save state
    const [createdReport, setCreatedReport] = useState(null); // full detail from GET /:report_id
    const [selectedClientId, setSelectedClientId] = useState('');
    const [clients, setClients] = useState([]);
    const [selectedContactIds, setSelectedContactIds] = useState([]);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState('');

    const orderedCartBills = useMemo(
        () => sortBillsByMode(Array.from(cart.values()).map(e => e.bill), sortMode),
        [cart, sortMode]
    );

    const selectedTeam = eligibleTeams.find(t => t.team_id === selectedTeamId || t.TeamAccount?.team_id === selectedTeamId);

    const canAdvanceTeam = selectedTeamId !== null;
    const canAdvanceBills = cart.size > 0;
    const canCreate = title.trim().length > 0 && !creating;

    const canAdvanceCurrent = STEPS[step] === 'Team' ? canAdvanceTeam
        : STEPS[step] === 'Bills & Notes' ? canAdvanceBills
        : true;

    const handleCreate = async () => {
        setCreateError('');
        setCreating(true);
        try {
            const bills = orderedCartBills.map((bill, i) => ({
                bill_id: bill.bill_id,
                sort_order: i,
                note: cart.get(bill.bill_id)?.note || null,
            }));
            const res = await API.createReport({
                team_id: selectedTeamId,
                title: title.trim(),
                intro_message: isIntroMessageEmpty(introMessage) ? null : introMessage,
                sort_mode: sortMode,
                show_digest: showDigest,
                show_ai_summary: showAiSummary,
                bills,
            });
            const detail = await API.getReportDetail(res.data.id);
            setCreatedReport(detail.data);
            onCreated?.();
        } catch (err) {
            setCreateError(err.response?.data?.msg || 'Failed to create report. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const loadClients = () => {
        if (clients.length > 0 || !selectedTeamId) return;
        API.getTeamClients(selectedTeamId)
            .then(res => setClients(res.data.filter(c => !c.archived)))
            .catch(err => console.error('Error loading clients:', err));
    };

    const toggleContact = (contactId) => {
        setSelectedContactIds(prev =>
            prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
        );
    };

    const handleSend = async () => {
        if (!selectedClientId || selectedContactIds.length === 0) return;
        setSending(true);
        setSendResult('');
        try {
            await API.sendReport(createdReport.id, {
                client_id: selectedClientId,
                contact_ids: selectedContactIds,
            });
            setSendResult('Report sent successfully.');
            setSelectedContactIds([]);
        } catch (err) {
            setSendResult(err.response?.data?.msg || 'Failed to send report.');
        } finally {
            setSending(false);
        }
    };

    const selectedClient = clients.find(c => String(c.client_id) === String(selectedClientId));
    const contactsWithEmail = (selectedClient?.TeamClientContacts || []).filter(c => c.email);

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable crm-dialog"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header crm-header">
                        <h5 className="modal-title crm-title">
                            {createdReport ? 'Report Created' : 'Build Report'}
                        </h5>
                    </div>

                    {!createdReport && (
                        <div className="crm-steps">
                            {STEPS.map((label, i) => (
                                <button
                                    key={label}
                                    className={`crm-step-tab${step === i ? ' crm-step-tab--active' : ''}${i < step ? ' crm-step-tab--done' : ''}`}
                                    onClick={() => i < step && setStep(i)}
                                    disabled={i > step}
                                >
                                    <span className="crm-step-num">{i < step ? '✓' : i + 1}</span>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="modal-body crm-body">
                        {!createdReport && STEPS[step] === 'Team' && (
                            <div>
                                <p className="crm-step-desc">Which Workspace is this report for?</p>
                                <div className="crm-team-list">
                                    {eligibleTeams.map(m => {
                                        const team = m.TeamAccount;
                                        const isSelected = selectedTeamId === team.team_id;
                                        return (
                                            <div
                                                key={team.team_id}
                                                className={`crm-team-row${isSelected ? ' crm-team-row--selected' : ''}`}
                                                onClick={() => setSelectedTeamId(team.team_id)}
                                            >
                                                <span>{team.team_name}</span>
                                                {isSelected && <i className="bi bi-check2 crm-team-check" />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {!createdReport && STEPS[step] === 'Bills & Notes' && (
                            <div>
                                <p className="crm-step-desc">
                                    Review the bills in this report, in the order they'll appear ({sortMode === 'calendar' ? 'Calendar View' : 'current sort'}).
                                    Add an optional note to convey something specific to the client/stakeholder.
                                </p>
                                {orderedCartBills.length === 0 ? (
                                    <p className="text-muted small fst-italic">No bills added yet.</p>
                                ) : (
                                    <div className="crm-bill-list">
                                        {orderedCartBills.map(bill => {
                                            const { billDisplay } = deriveBillDisplayFields(bill);
                                            const note = cart.get(bill.bill_id)?.note || '';
                                            return (
                                                <div key={bill.bill_id} className="crm-bill-row">
                                                    <div className="crm-bill-row-header">
                                                        <div>
                                                            <span className="crm-bill-num">{billDisplay}</span>
                                                            <span className="crm-bill-name ms-2">{bill.title}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="crm-remove-btn"
                                                            onClick={() => onRemoveBill(bill)}
                                                            title="Remove from report"
                                                        >
                                                            <i className="bi bi-x-lg" />
                                                        </button>
                                                    </div>
                                                    <textarea
                                                        className="form-control form-control-sm mt-1"
                                                        rows={2}
                                                        placeholder="Optional note for this bill…"
                                                        value={note}
                                                        onChange={e => onNoteChange(bill.bill_id, e.target.value)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {!createdReport && STEPS[step] === 'Format' && (
                            <div>
                                <div className="mb-3">
                                    <label className="crm-field-label">Report Title <span className="text-danger">*</span></label>
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="e.g. Q3 Legislative Update"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        maxLength={255}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="crm-field-label">Intro Message <span className="text-muted fw-normal text-lowercase">(optional)</span></label>
                                    <p className="crm-field-hint">Add a note to introduce this report to your client or stakeholder.</p>
                                    <ReactQuill
                                        className="crm-intro-editor"
                                        theme="snow"
                                        value={introMessage}
                                        onChange={setIntroMessage}
                                        placeholder="e.g. Here's our latest roundup of bills we're tracking on your behalf…"
                                        modules={INTRO_QUILL_MODULES}
                                        formats={INTRO_QUILL_FORMATS}
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="crm-field-label d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input crm-check"
                                            checked={showDigest}
                                            onChange={e => setShowDigest(e.target.checked)}
                                        />
                                        Include Digest Summaries
                                    </label>
                                </div>
                                <div className="mb-3">
                                    <label className="crm-field-label d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input crm-check"
                                            checked={showAiSummary}
                                            onChange={e => setShowAiSummary(e.target.checked)}
                                        />
                                        Include AI Summaries
                                    </label>
                                </div>

                                <div className="crm-summary">
                                    <div className="crm-summary-row">
                                        <span className="crm-summary-label">Workspace</span>
                                        <span>{selectedTeam?.TeamAccount?.team_name || selectedTeam?.team_name}</span>
                                    </div>
                                    <div className="crm-summary-row">
                                        <span className="crm-summary-label">Bills</span>
                                        <span>{cart.size}</span>
                                    </div>
                                </div>

                                {createError && (
                                    <div className="alert alert-danger py-2 mt-2 small">{createError}</div>
                                )}
                            </div>
                        )}

                        {createdReport && (
                            <div>
                                <p className="crm-step-desc">
                                    <strong>{createdReport.title}</strong> has been saved to your Workspace's Reports.
                                </p>

                                <div className="crm-postsave-actions mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => downloadReportAsPDF(createdReport, createdReport.bills)}
                                    >
                                        <i className="bi bi-file-earmark-pdf me-1" /> Download PDF
                                    </button>
                                </div>

                                <div className="crm-send-section">
                                    <p className="crm-field-label mb-2">Send to Contacts</p>
                                    <select
                                        className="form-select form-select-sm mb-2"
                                        value={selectedClientId}
                                        onFocus={loadClients}
                                        onChange={e => { setSelectedClientId(e.target.value); setSelectedContactIds([]); }}
                                    >
                                        <option value="">Select a client…</option>
                                        {clients.map(c => (
                                            <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                                        ))}
                                    </select>

                                    {selectedClientId && contactsWithEmail.length === 0 && (
                                        <p className="text-muted small fst-italic">No contacts with an email address for this client.</p>
                                    )}

                                    {selectedClientId && contactsWithEmail.length > 0 && (
                                        <div className="crm-contact-list mb-2">
                                            {contactsWithEmail.map(contact => (
                                                <label key={contact.contact_id} className="crm-contact-row">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input crm-check"
                                                        checked={selectedContactIds.includes(contact.contact_id)}
                                                        onChange={() => toggleContact(contact.contact_id)}
                                                    />
                                                    <span>{contact.first_name} {contact.last_name}</span>
                                                    <span className="text-muted small ms-2">{contact.email}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {selectedClientId && contactsWithEmail.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm crm-send-btn"
                                            disabled={selectedContactIds.length === 0 || sending}
                                            onClick={handleSend}
                                        >
                                            {sending ? 'Sending…' : 'Send'}
                                        </button>
                                    )}

                                    {sendResult && <p className="small mt-2 mb-0">{sendResult}</p>}
                                </div>

                                <Link
                                    to={`/teams?team=${selectedTeamId}&tab=reports`}
                                    className="crm-reports-link mt-3 d-inline-block"
                                >
                                    View in Workspace Reports
                                </Link>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer crm-footer">
                        {!createdReport && step > 0 && (
                            <button className="btn crm-back-btn" onClick={() => setStep(s => s - 1)}>Back</button>
                        )}
                        <button className="btn crm-cancel-btn" onClick={onClose}>
                            {createdReport ? 'Done' : 'Cancel'}
                        </button>
                        {!createdReport && (
                            step < STEPS.length - 1 ? (
                                <button
                                    className="btn crm-next-btn"
                                    onClick={() => setStep(s => s + 1)}
                                    disabled={!canAdvanceCurrent}
                                >
                                    Next
                                </button>
                            ) : (
                                <button className="btn crm-send-btn" onClick={handleCreate} disabled={!canCreate}>
                                    {creating ? 'Creating…' : 'Create Report'}
                                </button>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
